import React, { useMemo } from 'react';
import {
  LuArrowUpDown,
  LuBadgeCheck,
  LuImage,
  LuMapPin,
  LuShield,
  LuShieldCheck,
  LuShieldPlus,
  LuSignal,
  LuSparkles,
  LuTriangleAlert,
  LuTruck,
  LuUsers,
} from 'react-icons/lu';
import './UnitsGarageGrid.css';

const STATUS_VARIANTS = {
  disponible: { label: 'Disponible', tone: 'success' },
  available: { label: 'Disponible', tone: 'success' },
  reservada: { label: 'Reservada', tone: 'warning' },
  reserved: { label: 'Reservada', tone: 'warning' },
  mantenimiento: { label: 'Mantenimiento', tone: 'danger' },
  maintenance: { label: 'Mantenimiento', tone: 'danger' },
  'no disponible': { label: 'No disponible', tone: 'muted' },
  unavailable: { label: 'No disponible', tone: 'muted' },
};

const BOOLEAN_PERKS = [
  { key: 'gps', label: 'GPS' },
  { key: 'insured', label: 'Asegurado' },
  { key: 'liftGate', label: 'Lift gate' },
  { key: 'cTpat', label: 'C-TPAT' },
  { key: 'eTracking', label: 'E-Tracking' },
  { key: 'armored', label: 'Blindada' },
  { key: 'teamDrivers', label: 'Equipo dual' },
  { key: 'hazmat', label: 'Hazmat' },
];

const TAG_ICON_MAP = {
  gps: LuMapPin,
  localization: LuMapPin,
  tracking: LuSignal,
  'e-tracking': LuSignal,
  'e tracking': LuSignal,
  asegurado: LuShieldCheck,
  asegurada: LuShieldCheck,
  seguro: LuShieldCheck,
  'lift gate': LuArrowUpDown,
  liftgate: LuArrowUpDown,
  'c-tpat': LuShield,
  ctpat: LuShield,
  'c tpat': LuShield,
  hazmat: LuTriangleAlert,
  blindada: LuShieldPlus,
  armored: LuShieldPlus,
  'team drivers': LuUsers,
  'dual driver': LuUsers,
  'equipo dual': LuUsers,
};

const MAX_PERKS_SHOWN = 6;

const normalizeValue = value => (typeof value === 'string' ? value.trim() : value);

const normalizeKey = value =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const getTagIcon = label => {
  const normalized = normalizeKey(label);
  return TAG_ICON_MAP[normalized] || LuSparkles;
};

const parseTags = tags => {
  if (Array.isArray(tags)) {
    return tags
      .map(tag => {
        if (!tag) return '';
        if (typeof tag === 'string') return tag;
        if (typeof tag === 'object') {
          return tag.label || tag.name || '';
        }
        return '';
      })
      .map(tag => tag.trim())
      .filter(Boolean);
  }
  if (typeof tags === 'string' && tags.trim()) {
    const trimmed = tags.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parseTags(parsed);
      } catch {
        return trimmed
          .split(',')
          .map(entry => entry.trim())
          .filter(Boolean);
      }
    }
    return trimmed
      .split(',')
      .map(entry => entry.trim())
      .filter(Boolean);
  }
  return [];
};

const buildPerks = unit => {
  const declaredTags = parseTags(unit?.tags);
  const booleanTags = BOOLEAN_PERKS.filter(item => Boolean(unit?.[item.key])).map(
    item => item.label
  );
  const customEquipment = Array.isArray(unit?.customEquipment)
    ? unit.customEquipment
        .map(entry => (typeof entry === 'string' ? entry.trim() : ''))
        .filter(Boolean)
    : [];

  const collection = [...declaredTags, ...booleanTags, ...customEquipment];
  const unique = [];

  collection.forEach(label => {
    const normalized = normalizeKey(label);
    if (!normalized) return;
    if (!unique.some(item => item.key === normalized)) {
      unique.push({ key: normalized, label: label.trim() });
    }
  });

  return unique.slice(0, MAX_PERKS_SHOWN);
};

const formatDateLabel = value => {
  if (!value) return 'Sin fecha';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin fecha';
  return date.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const buildTitle = unit => {
  const year = normalizeValue(unit?.year);
  const descriptor =
    normalizeValue(unit?.model) ||
    normalizeValue(unit?.tractorType) ||
    normalizeValue(unit?.name);
  const combined = [year, descriptor].filter(Boolean).join(' ');
  return combined || 'Unidad sin nombre';
};

const buildSubtitle = unit => {
  const parts = [];
  if (unit?.tractorType && unit?.tractorType !== unit?.model) {
    parts.push(unit.tractorType);
  }
  if (unit?.company) {
    parts.push(unit.company);
  }
  return parts.join(' • ');
};

const resolveStatusVariant = status => {
  const normalized = normalizeKey(status);
  return STATUS_VARIANTS[normalized] || {
    label: status || 'Sin estado',
    tone: 'muted',
  };
};

const isUnitForSale = unit => {
  if (unit?.isForSale) return true;
  const normalizedCandidates = [
    unit?.listingType,
    unit?.listingStatus,
    unit?.marketStatus,
    unit?.status,
  ]
    .map(entry => (typeof entry === 'string' ? entry.trim().toLowerCase() : ''))
    .filter(Boolean);
  return normalizedCandidates.some(value =>
    ['sale', 'for-sale', 'venta', 'en-venta'].includes(value)
  );
};

const UnitsGarageGrid = ({ units, onEdit, isLoading }) => {
  const safeUnits = useMemo(() => (Array.isArray(units) ? units : []), [units]);
  const hasUnits = safeUnits.length > 0;
  const showSkeleton = Boolean(isLoading) && !hasUnits;
  const showEmpty = !isLoading && !hasUnits;

  const handleEdit = (unitItem) => {
    if (typeof onEdit === 'function') {
      onEdit(unitItem);
    }
  };

  const renderCard = unit => {
    const statusVariant = resolveStatusVariant(unit?.status);
    const perks = buildPerks(unit);
    const updatedLabel = formatDateLabel(unit?.updated || unit?.updated_at || unit?.updatedAt);
    const photo = unit?.photoUrl || unit?.photo || unit?.imageUrl || '';
    const subtitle = buildSubtitle(unit);
    const showPrice = isUnitForSale(unit) && unit?.priceLabel;

    return (
      <article key={unit?.id || unit?.economicNumber || buildTitle(unit)} className="garage-card" role="listitem">
        <div className="garage-card__media">
          {photo ? (
            <img src={photo} alt={buildTitle(unit)} className="garage-card__image" loading="lazy" />
          ) : (
            <div className="garage-card__placeholder" aria-label="Unidad sin foto">
              <LuTruck aria-hidden="true" />
            </div>
          )}
          <span
            className={`garage-card__status-chip garage-card__status-chip--${statusVariant.tone}`}
          >
            {statusVariant.label}
          </span>
        </div>

        <div className="garage-card__body">
          <div className="garage-card__title-row">
            <h3 className="garage-card__title">{buildTitle(unit)}</h3>
            {unit?.economicNumber ? (
              <span className="garage-card__eco">ECO {unit.economicNumber}</span>
            ) : null}
          </div>
          {subtitle ? <p className="garage-card__subtitle">{subtitle}</p> : null}
          <div className="garage-card__stats">
            {unit?.capacity ? (
              <span className="garage-card__stat" title="Capacidad">
                <LuBadgeCheck aria-hidden="true" />
                {unit.capacity}
              </span>
            ) : null}
            {showPrice ? (
              <span className="garage-card__price" title="Tarifa">
                <LuSparkles aria-hidden="true" />
                {unit.priceLabel}
              </span>
            ) : null}
          </div>
          {perks.length ? (
            <div className="garage-card__perks" aria-label="Perks activos">
              {perks.map(perk => {
                const Icon = getTagIcon(perk.label);
                return (
                  <span key={perk.key} className="garage-card__perk">
                    <Icon aria-hidden="true" />
                    <span>{perk.label}</span>
                  </span>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="garage-card__footer">
          <div className="garage-card__footer-row">
            <div className="garage-card__meta">Actualizada {updatedLabel}</div>
            <button
              type="button"
              className="garage-card__action"
              onClick={() => handleEdit(unit)}
            >
              Gestionar
            </button>
          </div>
        </div>
      </article>
    );
  };

  if (showSkeleton) {
    return (
      <div className="units-garage-grid units-garage-grid--loading">
        {Array.from({ length: 6 }).map((_, index) => (
          <article key={`garage-skeleton-${index}`} className="garage-card garage-card--skeleton" aria-hidden="true">
            <div className="garage-card__media">
              <div className="garage-card__skeleton-block" />
            </div>
            <div className="garage-card__body">
              <div className="garage-card__skeleton-line" />
              <div className="garage-card__skeleton-line garage-card__skeleton-line--short" />
              <div className="garage-card__skeleton-pill-group">
                <span className="garage-card__skeleton-pill" />
                <span className="garage-card__skeleton-pill" />
                <span className="garage-card__skeleton-pill" />
              </div>
            </div>
            <div className="garage-card__footer">
              <div className="garage-card__skeleton-line garage-card__skeleton-line--short" />
            </div>
            <div className="garage-card__action garage-card__action--disabled" />
          </article>
        ))}
      </div>
    );
  }

  if (showEmpty) {
    return (
      <div className="units-garage-empty" role="status">
        <div className="units-garage-empty__media">
          <LuImage aria-hidden="true" />
        </div>
        <div>
          <h3>Sin unidades registradas</h3>
          <p>Agrega tu primera unidad para ver el garage en accion.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="units-garage-grid" role="list" aria-live={isLoading ? 'polite' : 'off'}>
      {safeUnits.map(renderCard)}
    </div>
  );
};

export default UnitsGarageGrid;
